/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Alert, Box, Typography, Button } from '@mui/material';
import { TrendingUp as UpgradeIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { StudioCreateForm } from '../../studios/forms';
import { useCreateStudioMutation } from '../../../features/studios';
import { StudioFormData } from '../../../types/studioForms';
import { CreateStudioRequest } from '../../../types/studio';
import { selectCurrentUser } from '../../../features/auth';
import { useSubscriptionLimits } from '../../../hooks/useSubscriptionLimits';
import Header from '../../layout/Header';
import UsageIndicator from '../../subscription/UsageIndicator';
import { YouTubeVideoButton } from '../../common';

/**
 * Studio creation page component.
 * Provides a dedicated page for creating new studios.
 */
const StudioCreate: React.FC = () => {
  const { t } = useTranslation('studio');
  const navigate = useNavigate();
  const currentUser = useSelector(selectCurrentUser);
  const [createStudio, { isLoading, error }] = useCreateStudioMutation();

  // Get subscription limits for studio creation
  const { canCreateStudio, isAtStudioLimit, remainingStudios } =
    useSubscriptionLimits();

  // Handle upgrade navigation
  const handleUpgrade = () => {
    navigate('/pricing', {
      state: {
        upgradeContext: 'studio_limit',
        currentAction: 'studio_creation',
      },
    });
  };

  // Handle form submission
  const handleSubmit = async (formData: StudioFormData) => {
    // Enhanced debugging for studio creation
    if (process.env.NODE_ENV === 'development') {
      console.group('[Studio Creation] Form submission started');
      console.log('Form data received:', {
        name: formData.name,
        description: formData.description,
        businessInfo: formData.businessInfo,
        hasLogo: !!formData.logo,
        logoType: formData.logo?.type,
        logoSize: formData.logo?.size,
      });
    }

    try {
      // Auto-populate email from authenticated user
      const userEmail = currentUser?.email;
      if (!userEmail) {
        throw new Error(
          'User email not available. Please ensure you are logged in.'
        );
      }

      // Convert form data to API request format (now includes logo and email from authenticated user)
      const createRequest: CreateStudioRequest = {
        name: formData.name,
        description: formData.description,
        email: userEmail, // Use authenticated user's email
        businessInfo: formData.businessInfo,
        logo: formData.logo || undefined,
        allowJoinRequests: formData.allowJoinRequests,
        joinRequestMessage: formData.joinRequestMessage,
      };

      if (process.env.NODE_ENV === 'development') {
        console.log('API request payload:', {
          name: createRequest.name,
          description: createRequest.description,
          businessInfo: createRequest.businessInfo,
          hasLogo: !!createRequest.logo,
        });
        console.log('Making POST request to /studios/ with logo included');
      }

      // Create the studio (logo is now included in the initial request)
      const result = await createStudio(createRequest).unwrap();

      if (process.env.NODE_ENV === 'development') {
        console.log('Studio creation successful:', {
          studioId: result.id,
          studioName: result.name,
          logoIncluded: !!formData.logo,
        });
        console.log(`Navigating to /studio/${result.id}`);
        console.groupEnd();
      }

      // Navigate to the new studio
      navigate(`/studio/${result.id}`);
    } catch (submitError) {
      // Enhanced error logging with full HTML content capture
      if (process.env.NODE_ENV === 'development') {
        console.error('Studio creation failed with error:', submitError);

        // Log additional error details if available
        if (submitError && typeof submitError === 'object') {
          const errorObj = submitError as any;
          console.error('Error details:', {
            status: errorObj.status,
            data: errorObj.data,
            originalStatus: errorObj.originalStatus,
          });

          // Special handling for 500 errors with HTML content
          if (errorObj.status === 500 || errorObj.originalStatus === 500) {
            console.group('[500 ERROR] Server Error Details');
            console.error('Status:', errorObj.status);
            console.error('Original Status:', errorObj.originalStatus);

            // Log the full HTML content if it's a parsing error
            if (errorObj.status === 'PARSING_ERROR' && errorObj.data) {
              console.error(
                'HTML Response Content (first 2000 chars):',
                typeof errorObj.data === 'string'
                  ? errorObj.data.substring(0, 2000)
                  : JSON.stringify(errorObj.data).substring(0, 2000)
              );

              // Try to extract error information from HTML
              if (typeof errorObj.data === 'string') {
                const htmlContent = errorObj.data;

                // Look for common Django error patterns
                const titleMatch = htmlContent.match(/<title>(.*?)<\/title>/i);
                const h1Match = htmlContent.match(/<h1[^>]*>(.*?)<\/h1>/i);
                const errorMatch = htmlContent.match(
                  /class="exception_value"[^>]*>(.*?)</i
                );
                const traceMatch = htmlContent.match(
                  /class="traceback"[^>]*>(.*?)</i
                );

                if (titleMatch) {
                  console.error('HTML Title:', titleMatch[1]);
                }
                if (h1Match) {
                  console.error('HTML H1:', h1Match[1]);
                }
                if (errorMatch) {
                  console.error('Django Exception:', errorMatch[1]);
                }
                if (traceMatch) {
                  console.error(
                    'Django Traceback Found:',
                    'Check full HTML content above'
                  );
                }

                // Look for specific error keywords
                if (htmlContent.includes('IntegrityError')) {
                  console.error(
                    'DATABASE ERROR: IntegrityError detected - likely constraint violation'
                  );
                }
                if (htmlContent.includes('ValidationError')) {
                  console.error('VALIDATION ERROR: ValidationError detected');
                }
                if (htmlContent.includes('PermissionDenied')) {
                  console.error('PERMISSION ERROR: PermissionDenied detected');
                }
                if (htmlContent.includes('DoesNotExist')) {
                  console.error('NOT FOUND ERROR: DoesNotExist detected');
                }
              }
            }
            console.groupEnd();
          }
        }
        console.groupEnd();
      }

      // Error is handled by the mutation and passed to the form
      console.error('Studio creation failed:', submitError);
    }
  };

  // Handle form cancellation
  const handleCancel = () => {
    navigate('/dashboard');
  };

  // Get error message with detailed error handling
  const getErrorMessage = () => {
    if (error) {
      // Handle 500 errors and parsing errors specifically
      if ('status' in error) {
        // Handle server errors (500+)
        if (typeof error.status === 'number' && error.status >= 500) {
          return t('createPage.errors.server');
        }

        // Handle parsing errors (usually from HTML responses)
        if (error.status === 'PARSING_ERROR') {
          return t('createPage.errors.parsing');
        }

        // Handle network errors
        if (error.status === 'FETCH_ERROR') {
          return t('createPage.errors.network');
        }
      }

      if ('data' in error && error.data) {
        // Handle API error response
        const errorData = error.data as any;

        // Handle CSRF-related errors
        if (
          errorData.detail?.includes('CSRF') ||
          errorData.detail?.includes('Forbidden')
        ) {
          return t('createPage.errors.csrf');
        }

        // Handle authentication errors
        if (error.status === 401) {
          return t('createPage.errors.unauthorized');
        }

        // Handle permission errors
        if (error.status === 403) {
          return t('createPage.errors.forbidden');
        }

        // Handle validation errors
        if (errorData.name) {
          return t('createPage.errors.nameError', {
            error: Array.isArray(errorData.name)
              ? errorData.name[0]
              : errorData.name,
          });
        }

        if (errorData.description) {
          return t('createPage.errors.descriptionError', {
            error: Array.isArray(errorData.description)
              ? errorData.description[0]
              : errorData.description,
          });
        }

        if (errorData.logo) {
          return t('createPage.errors.logoError', {
            error: Array.isArray(errorData.logo)
              ? errorData.logo[0]
              : errorData.logo,
          });
        }

        if (errorData.business_info || errorData.businessInfo) {
          const businessErrors =
            errorData.business_info || errorData.businessInfo;
          const firstError = Object.values(businessErrors)[0];
          return t('createPage.errors.businessInfoError', {
            error: Array.isArray(firstError) ? firstError[0] : firstError,
          });
        }

        // Handle general API errors
        if (errorData.detail) {
          return errorData.detail;
        }

        if (errorData.non_field_errors) {
          return Array.isArray(errorData.non_field_errors)
            ? errorData.non_field_errors[0]
            : errorData.non_field_errors;
        }

        // Handle rate limiting
        if (error.status === 429) {
          return t('createPage.errors.rateLimited');
        }
      }

      // Fallback error message
      return t('createPage.errors.general');
    }
    return null;
  };

  // Show limit reached message if user cannot create studio
  if (isAtStudioLimit) {
    return (
      <>
        <Header />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h4" component="h1" gutterBottom color="error">
              {t('createPage.limitReached.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {t('createPage.limitReached.description')}
            </Typography>

            {/* Studio Usage Indicator */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
              <UsageIndicator
                type="studios"
                current={0} // Will be populated with actual usage
                limit={0} // Will be populated with actual limits
                showProgress
              />
            </Box>

            <Alert severity="info" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
              <Typography variant="body2">
                <strong>{t('createPage.limitReached.needMoreStudios')}</strong>{' '}
                {t('createPage.limitReached.paidPlansOffer')}
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<UpgradeIcon />}
                onClick={handleUpgrade}
                color="primary"
              >
                {t('createPage.limitReached.upgradeButton')}
              </Button>
              <Button variant="outlined" size="large" onClick={handleCancel}>
                {t('createPage.limitReached.backButton')}
              </Button>
            </Box>
          </Box>
        </Container>
      </>
    );
  }

  return (
    <>
      <Header />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Studio Usage Warning */}
        {remainingStudios <= 1 && canCreateStudio && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>{t('createPage.limitWarning.title')}</strong>{' '}
              {t('createPage.limitWarning.message', {
                remaining: remainingStudios,
                count: remainingStudios,
              })}{' '}
              {t('createPage.limitWarning.upgradeMessage')}
            </Typography>
          </Alert>
        )}

        {/* Tutorial Video */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: 'action.hover',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Watch our step-by-step tutorial to learn how to create your own
            studio on tools.dance.
          </Typography>
          <YouTubeVideoButton
            videoId="Ke3mmXrMpo0"
            videoTitle="How to Create a New Studio on tools.dance"
            buttonText="Watch Tutorial"
            variant="outlined"
            size="small"
          />
        </Box>

        <StudioCreateForm
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={isLoading}
          error={getErrorMessage()}
        />
      </Container>
    </>
  );
};

export default StudioCreate;
